/**
 * i18n messages for the expense tracker (en/ar). Registered in
 * core/plugins/i18n.ts via deep merge (same pattern as reportMessages).
 * CSP-safe compiler: only `{named}` placeholders — no plurals, no linked messages.
 */
export const expenseTrackerMessages = {
  en: {
    expenseTracker: {
      title: 'Financial Obligations',
      newSubmission: 'New Submission',
      editSubmission: 'Edit Submission',
      sections: {
        home: 'Home',
        utilities: 'Utilities',
        spouse: 'Spouse',
        food: 'Food',
        family_obligations: 'Family Obligations',
        transportation: 'Transportation',
        education: 'Education',
        personal_care: 'Personal Care',
        parents: 'Parents (if retired)',
        miscellaneous: 'Miscellaneous',
      },
      fields: {
        mortgage: 'Mortgage',
        rent: 'Rent',
        repair: 'Repairs / Maintenance',
        water: 'Water & Electricity',
        cooking: 'Cooking Gas',
        mobile: 'Mobile & Internet',
        allowance: 'Allowance',
        grocery: 'Grocery',
        child: 'Children',
        housemaid: 'Housemaid',
        car_insurance: 'Car Insurance (Annual)',
        petrol: 'Petrol',
        fees: 'Fees',
        tuition: 'Tuition',
        clothing: 'Clothing',
        parents_allowance: 'Parents Allowance',
        parents_home: 'Parents Home',
        home_insurance: 'Home Insurance / Service Charges',
        sub_letting: 'Rental Income from Sub-letting an Owned Residence',
        car_loan: 'Car Loan Payment',
        school_transport: 'School Transport',
      },
      hints: {
        mortgage: 'Excluded from allowance if covered by mandatory life insurance.',
        sub_letting: 'Required disclosure — deducted from the total.',
      },
      ui: {
        current: 'Current',
        changesCount: '+{count} changes',
        oneChange: '+1 change',
        noChanges: 'No changes',
        changed: 'Changed',
        viewDetails: 'View details',
        edit: 'Edit',
        addFileHint: 'PDF or image',
        total: 'Total',
        grandTotal: 'Grand Total',
        showingOf: 'Showing {shown} of {total}',
        loadMore: 'Load more',
        empty: 'No submissions yet. Create your first one to start tracking.',
        save: 'Save Submission',
        saving: 'Saving submission…',
        update: 'Update Submission',
        updating: 'Updating submission…',
        cancel: 'Cancel',
        note: 'Note',
        notePlaceholder: 'Optional note',
        attachments: 'Attachments',
        addFile: 'Add file',
        reveal: 'Reveal amounts',
        hide: 'Hide amounts',
        revealOne: 'Reveal amount',
        item: 'Item',
        amount: 'Amount',
        previous: 'Previous',
        details: 'Details',
        created: 'Created',
        delete: 'Delete',
        changedFrom: '{old} → {new}',
        loadError: 'Could not load expense data.',
        saveError: 'Could not save the submission. Please check the amounts and try again.',
        updateError: 'Could not update the submission. Only the latest entry can be edited.',
        uploadError: 'Could not upload the attachment.',
        viewAttachment: 'View',
        miscTooltip:
          "Add one-off or recurring costs that don't fit the categories above — name each in your own words.",
        miscLabelPlaceholder: 'Name this item',
        miscEmpty: "No items yet — add anything that doesn't fit the categories above.",
        miscLabelRequired:
          'Please name every miscellaneous item, or remove the empty ones, before saving.',
        addItem: 'Add item',
        removeItem: 'Remove item',
        added: 'Added',
        removed: 'Removed',
      },
      reviewer: {
        title: 'Financial Obligations Review',
        subtitle: 'Select a user to review their submitted financial obligations.',
        searchPlaceholder: 'Search users by name or email…',
        loading: 'Loading…',
        exportExcel: 'Export to Excel',
        exporting: 'Exporting…',
        noSubmissions: '{name} has no financial-obligation submissions.',
      },
    },
  },
  ar: {
    expenseTracker: {
      title: 'الالتزامات المالية',
      newSubmission: 'إدخال جديد',
      editSubmission: 'تعديل الإدخال',
      sections: {
        home: 'المنزل',
        utilities: 'المرافق',
        spouse: 'الزوج/الزوجة',
        food: 'الطعام',
        family_obligations: 'التزامات الأسرة',
        transportation: 'المواصلات',
        education: 'التعليم',
        personal_care: 'العناية الشخصية',
        parents: 'الوالدان (عند التقاعد)',
        miscellaneous: 'متفرقات',
      },
      fields: {
        mortgage: 'قرض المنزل',
        rent: 'الإيجار',
        repair: 'الإصلاح / الصيانة',
        water: 'الماء والكهرباء',
        cooking: 'غاز الطبخ',
        mobile: 'الهاتف والإنترنت',
        allowance: 'المصروف',
        grocery: 'البقالة',
        child: 'الأطفال',
        housemaid: 'الخادمة',
        car_insurance: 'تأمين السيارة (سنوي)',
        petrol: 'الوقود',
        fees: 'الرسوم',
        tuition: 'الدراسة',
        clothing: 'الملابس',
        parents_allowance: 'مصروف الوالدين',
        parents_home: 'سكن الوالدين',
        home_insurance: 'تأمين المنزل / رسوم الخدمات',
        sub_letting: 'دخل الإيجار من تأجير سكن مملوك',
        car_loan: 'قسط قرض السيارة',
        school_transport: 'النقل المدرسي',
      },
      hints: {
        mortgage: 'يُستثنى من البدل إذا كان مغطى بتأمين حياة إلزامي.',
        sub_letting: 'إفصاح مطلوب — يُخصم من المجموع.',
      },
      ui: {
        current: 'الحالي',
        changesCount: '+{count} تغييرات',
        oneChange: '+تغيير واحد',
        noChanges: 'لا تغييرات',
        changed: 'تغيّر',
        viewDetails: 'عرض التفاصيل',
        edit: 'تعديل',
        addFileHint: 'ملف PDF أو صورة',
        total: 'المجموع',
        grandTotal: 'المجموع الكلي',
        showingOf: 'عرض {shown} من {total}',
        loadMore: 'تحميل المزيد',
        empty: 'لا توجد إدخالات بعد. أنشئ أول إدخال لبدء التتبع.',
        save: 'حفظ الإدخال',
        saving: 'جارٍ الحفظ…',
        update: 'تحديث الإدخال',
        updating: 'جارٍ التحديث…',
        cancel: 'إلغاء',
        note: 'ملاحظة',
        notePlaceholder: 'ملاحظة اختيارية',
        attachments: 'المرفقات',
        addFile: 'إضافة ملف',
        reveal: 'إظهار المبالغ',
        hide: 'إخفاء المبالغ',
        revealOne: 'إظهار المبلغ',
        item: 'البند',
        amount: 'المبلغ',
        previous: 'السابق',
        details: 'التفاصيل',
        created: 'أُنشئ',
        delete: 'حذف',
        changedFrom: '{old} ← {new}',
        loadError: 'تعذّر تحميل بيانات المصروفات.',
        saveError: 'تعذّر حفظ الإدخال. يرجى التحقق من المبالغ والمحاولة مرة أخرى.',
        updateError: 'تعذّر تحديث الإدخال. يمكن تعديل أحدث إدخال فقط.',
        uploadError: 'تعذّر رفع المرفق.',
        viewAttachment: 'عرض',
        miscTooltip:
          'أضف تكاليف لمرة واحدة أو متكررة لا تندرج ضمن الفئات أعلاه — سمِّ كل بند بكلماتك.',
        miscLabelPlaceholder: 'سمِّ هذا البند',
        miscEmpty: 'لا توجد بنود بعد — أضف ما لا يندرج ضمن الفئات أعلاه.',
        miscLabelRequired: 'يرجى تسمية كل بند من المتفرقات، أو إزالة البنود الفارغة، قبل الحفظ.',
        addItem: 'إضافة بند',
        removeItem: 'إزالة البند',
        added: 'مضاف',
        removed: 'محذوف',
      },
      reviewer: {
        title: 'مراجعة الالتزامات المالية',
        subtitle: 'اختر مستخدمًا لمراجعة التزاماته المالية المُقدّمة.',
        searchPlaceholder: 'ابحث عن المستخدمين بالاسم أو البريد الإلكتروني…',
        loading: 'جارٍ التحميل…',
        exportExcel: 'تصدير إلى Excel',
        exporting: 'جارٍ التصدير…',
        noSubmissions: 'لا توجد التزامات مالية مُقدّمة لـ {name}.',
      },
    },
  },
}
